import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, type PhoneMetrics } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

// No external deps needed — this screen is self-contained.
export interface PhoneAddFriendInlineDeps {
  // intentionally empty — no this.xxx callbacks used
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneAddFriendInlineScreen(
  _deps: PhoneAddFriendInlineDeps,
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;

  // Title
  ctx.fillStyle = "#4dff88";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("ADD FRIEND", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  // Divider
  ctx.fillStyle = "#2a3a55";
  for (let dx = contentX; dx < contentRight; dx += 4) {
    ctx.fillRect(dx, curY, 2, 1);
  }
  curY += metrics.sectionGap;

  // Description
  ctx.fillStyle = "#7fa0c2";
  ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("ENTER THEIR USERNAME", scrX + scrW / 2, curY);
  curY += desktop ? 18 : 16;

  // Input field (taller)
  const inputH = desktop ? 32 : 28;
  const inputRect: Rect = { x: contentX, y: curY, w: contentW, h: inputH };
  uiRects.phoneAddFriendInput = inputRect;
  ctx.fillStyle = "#0f1628";
  ctx.fillRect(inputRect.x, inputRect.y, inputRect.w, inputRect.h);
  ctx.strokeStyle = "#5c8cd4";
  ctx.lineWidth = 1;
  ctx.strokeRect(inputRect.x, inputRect.y, inputRect.w, inputRect.h);
  curY += inputH + 10;

  // Error/success message
  if (model.friendsError) {
    const isSuccess = model.friendsError.includes("SENT");
    ctx.fillStyle = isSuccess ? "#4dff88" : "#ff6b6b";
    ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(model.friendsError, scrX + scrW / 2, curY);
  }

  // ── Bottom-pinned buttons ──
  const btnH = metrics.ctaH;
  const btnGap = 4;

  // BACK — full width at very bottom
  const backY = scrY + scrH - btnH - 6;
  const backRect: Rect = { x: contentX, y: backY, w: contentW, h: btnH };
  uiRects.phoneAddFriendBack = backRect;
  ctx.fillStyle = "#162030";
  ctx.fillRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.strokeStyle = "#5a7a9a";
  ctx.lineWidth = 1;
  ctx.strokeRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.fillStyle = "#8a9ab0";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);

  // SEND REQUEST — full width above BACK
  const sendY = backY - btnH - btnGap;
  const sendRect: Rect = { x: contentX, y: sendY, w: contentW, h: btnH };
  uiRects.phoneAddFriendSubmit = sendRect;
  ctx.fillStyle = "#0a2a10";
  ctx.fillRect(sendRect.x, sendRect.y, sendRect.w, sendRect.h);
  ctx.strokeStyle = "#4dff88";
  ctx.lineWidth = 1;
  ctx.strokeRect(sendRect.x, sendRect.y, sendRect.w, sendRect.h);
  ctx.fillStyle = "#4dff88";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SEND REQUEST", sendRect.x + sendRect.w / 2, sendRect.y + sendRect.h / 2);
}
